import {
  DirectionsCar,
  ExpandLess,
  ExpandMore,
  LocalShipping,
} from '@mui/icons-material'

import { ReactElement } from 'react'
import { I18n, Translate, Translate2 } from 'common'
import { breakpoints } from '../../../../theme/breakpoints'
import styled, { css } from 'styled-components'
import {
  useVehicleWidgetStore,
  VehicleWidgetDisplayState,
} from '../../VehicleSearch/store/VehicleWidgetStore'
import { defaultTheme } from '../../../../theme/index'
import { useSearchStore } from '../../../../store/search/SearchStore'
import { useNavigate } from 'react-router-dom'
import { StoreInstances } from '../../../../store/StoreInstancesContainer'
import ClearVehicleMenu from './ClearVehicleMenu'
import { LookupType } from '../../../../helpers/lookupType'
import { Vehicle } from '../../../../store/models/Vehicles'

interface VehicleInfoProps {
  vehicle: Vehicle
  onClick: () => void
  open: boolean
}

const VehicleInfo = (props: VehicleInfoProps): ReactElement => {
  const { vehicle, open, onClick } = props
  const { type, year, model, subModel, engine } = vehicle

  const vehicleWidgetStore = useVehicleWidgetStore()
  const searchStore = useSearchStore()
  const navigate = useNavigate()

  const vehicleSelected = year && model && engine

  const isHeavyDuty = type?.id === LookupType.HEAVY_DUTY

  const primaryText = vehicleSelected
    ? `${year.value} ${model.value}${subModel?.value ? ` ${subModel.value}` : ''}`
    : Translate('addVehicle')

  const handleDeleteVehicle = () => {
    if (
      window.location.pathname === '/partsCatalog' ||
      window.location.pathname === '/searchResults'
    ) {
      navigate('home')
    }
    searchStore.deleteCurrentVehicle()
    vehicleWidgetStore.setIsDrawerOpened(false)
    vehicleWidgetStore.setDisplayState(VehicleWidgetDisplayState.select)
    StoreInstances.uiStore.displaySuccessNotification(
      'vehicleSuccessfullyDeleted'
    )
  }

  const Icon = isHeavyDuty ? <LocalShippingIcon /> : <DirectionsCarIcon />

  const ExpandMoreIcon = <ExpandMore onClick={onClick} />
  const ExpandLessIcon = <ExpandLess onClick={onClick} />
  const ExpandIcon = open ? ExpandLessIcon : ExpandMoreIcon

  const SelectVehicleCTA = (
    <SelectVehicleText onClick={onClick}>
      <I18n name="selectVehicle" />
    </SelectVehicleText>
  )

  return (
    <Wrapper aria-controls="collapse-vehicle-widget" aria-expanded={open}>
      <Information>
        {Icon}
        <VehicleInformation>
          <MainText>{primaryText}</MainText>
          <ModelContainer>
            <SecondaryText>
              {engine?.value === 'All Engine'
                ? Translate2('allEngines')
                : engine?.value}
            </SecondaryText>
          </ModelContainer>
        </VehicleInformation>
      </Information>

      <CTAs>
        {!vehicleSelected && SelectVehicleCTA}

        {vehicleSelected && (
          <ClearVehicleRow>
            <ClearText
              onClick={() => {
                vehicleWidgetStore.setIsDrawerOpened(true)
                vehicleWidgetStore.setDisplayState(
                  VehicleWidgetDisplayState.edit
                )
              }}
            >
              <I18n name="change"></I18n>
            </ClearText>
            <ClearTextDevider></ClearTextDevider>
            <ClearText onClick={handleDeleteVehicle}>
              <I18n name="vehicleClear"></I18n>
            </ClearText>
            <ClearVehicleMenu></ClearVehicleMenu>
          </ClearVehicleRow>
        )}
        {ExpandIcon}
      </CTAs>
    </Wrapper>
  )
}

const Row = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  @media print {
    display: none;
  }
`

const VehicleInformation = styled.div`
  display: flex;
  flex-direction: column;
  align-items: initial;
  cursor: pointer;
  margin: auto 0;
  width: 100%;
  overflow: hidden;
`

const ClearVehicleRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;

  @media print {
    display: none;
  }

  @media screen and (${breakpoints.laptop}px < width <= ${breakpoints.laptopMedium}px) {
    max-width: fit-content;
    gap: 24px;
  }

  @media screen and (width < ${breakpoints.mobileLarge}px) {
    max-width: fit-content;
  }
`

const Wrapper = styled(Row)`
  height: 56px;
  padding: 0px 16px;
  border: 1px solid ${defaultTheme.colors.greyBorder};
  border-radius: 3px;
  width: 100%;
  justify-content: space-between;
  gap: 16px;
`

const CTAs = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
  gap: 16px;

  @media screen and (${breakpoints.laptopMedium}px < width) {
    width: 100%;
    min-width: 165px;
    max-width: 165px;
  }

  @media screen and (${breakpoints.tabletLarge}px <= width < ${breakpoints.laptopMedium}px) {
    width: 100%;
    min-width: 64px;
    max-width: 64px;
  }
`

const MainText = styled.div`
  align-items: left;
  font-family: Roboto;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const SecondaryText = styled.span`
  align-items: left;
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  line-height: 13px;
`

const ClearText = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-decoration: underline;
  text-transform: capitalize;
  @media (${breakpoints.tabletLarge}px <= width <= ${breakpoints.laptopMedium}px) {
    display: none;
  }

  @media screen and (width < ${breakpoints.mobileLarge}px) {
    display: none;
  }
`

const ClearTextDevider = styled.div`
  height: 24px;
  border: ${defaultTheme.fieldBorder};

  @media (${breakpoints.tabletLarge}px <= width <= ${breakpoints.laptopMedium}px) {
    display: none;
  }

  @media screen and (width < ${breakpoints.mobileLarge}px) {
    display: none;
  }
`

const SelectVehicleText = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-decoration: underline;
  text-wrap: nowrap;

  @media (${breakpoints.tabletLarge}px <= width <= ${breakpoints.laptopMedium}px) {
    display: none;
  }

  @media (width < ${breakpoints.mobileLarge}px) {
    display: none;
  }
`

const ModelContainer = styled.div`
  display: inline;
  width: 100%;
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  line-height: 13px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const BaseIcon = css`
  font-size: 32px;
`

const DirectionsCarIcon = styled(DirectionsCar)`
  ${BaseIcon}
`

const LocalShippingIcon = styled(LocalShipping)`
  ${BaseIcon}
`

const Information = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;

  @media screen and (${breakpoints.laptopMedium}px < width < ${breakpoints.laptopLarge}px) {
    min-width: 136px;
    max-width: 300px;
  }

  @media screen and (width < ${breakpoints.laptopMedium}px) {
    min-width: 158px;
    max-width: 275px;
  }
`

export default VehicleInfo
